import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash.omit';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  localElement: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  setLocalElement: PropTypes.func.isRequired,
  cardElementBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  cardBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  onCardElementUpdate: PropTypes.func.isRequired,
  className: PropTypes.string,
};

const CardDesignerCardElementDragControls = ({
  element,
  localElement,
  setLocalElement,
  cardElementBounds,
  cardBounds,
  onCardElementUpdate,
  className,
}) => {
  const { x: boundedX, y: boundedY } = cardElementBounds;
  const [initialPosition, setInitialPosition] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragData, setDragData] = useState(null);
  const [draggedElement, setDraggedElement] = useState(false);
  const [initialMousePosition, setInitialMousePosition] = useState({});
  const { frame } = localElement.cardElement;

  useEffect(() => {
    setInitialPosition({ x: boundedX, y: boundedY });
  }, [cardElementBounds]);

  useEffect(() => {
    const onDrag = event => {
      const { clientX, clientY } = event;
      const { x, y } = initialPosition;
      const { x: initialClientX = clientX, y: initialClientY = clientY } =
        initialMousePosition;
      const {
        x: xPercent,
        y: yPercent,
        width: widthPercent,
        height: heightPercent,
        rotation: frameRotation = 0,
      } = frame;

      // Account for frame rotation when computing the bounding box.
      // widthPercent is a fraction of the card's width, heightPercent is a
      // fraction of the card's height. When the card is not square these
      // represent different actual sizes, so we must scale through the
      // card's aspect ratio before applying the rotation formula.
      const cardWidth = cardBounds?.width || 1;
      const cardHeight = cardBounds?.height || 1;
      const aspectH = cardHeight / cardWidth; // height-to-width
      const aspectW = cardWidth / cardHeight; // width-to-height
      const absCos = Math.abs(Math.cos(frameRotation));
      const absSin = Math.abs(Math.sin(frameRotation));
      const boundingWidthPercent =
        widthPercent * absCos + heightPercent * aspectH * absSin;
      const boundingHeightPercent =
        widthPercent * aspectW * absSin + heightPercent * absCos;

      // The element's x/y position refers to the top-left of the unrotated
      // rectangle. CSS rotate() pivots around the center, so the rotated
      // bounding box extends beyond x/y by half the size difference.
      const offsetX = (boundingWidthPercent - widthPercent) / 2;
      const offsetY = (boundingHeightPercent - heightPercent) / 2;

      const newX = Math.floor(x + clientX - initialClientX);
      const newY = Math.floor(y + clientY - initialClientY);

      // Derive the percent directly from the card size. Scaling the current
      // percent by the element's own pixel offset breaks down at the card
      // edge, where the offset is 0.
      let newXPercent = Math.round((newX / cardWidth) * 1e3) / 1e3;
      let newYPercent = Math.round((newY / cardHeight) * 1e3) / 1e3;

      // Guard against NaN/Infinity from edge-case arithmetic
      if (!Number.isFinite(newXPercent)) newXPercent = xPercent;
      if (!Number.isFinite(newYPercent)) newYPercent = yPercent;

      // Clamp so the rotated bounding box stays within [0.001, 0.999].
      // The bounding box left edge is at (xPercent - offsetX) and the
      // right edge is at (xPercent - offsetX + boundingWidthPercent).
      const minXPercent = 0.001 + offsetX;
      const maxXPercent = 0.999 - boundingWidthPercent + offsetX;
      const minYPercent = 0.001 + offsetY;
      const maxYPercent = 0.999 - boundingHeightPercent + offsetY;

      if (minXPercent > maxXPercent) {
        // Bounding box is wider than the card — center the element.
        newXPercent = (minXPercent + maxXPercent) / 2;
      } else {
        if (newXPercent > maxXPercent) newXPercent = maxXPercent;
        if (newXPercent < minXPercent) newXPercent = minXPercent;
      }
      if (minYPercent > maxYPercent) {
        newYPercent = (minYPercent + maxYPercent) / 2;
      } else {
        if (newYPercent > maxYPercent) newYPercent = maxYPercent;
        if (newYPercent < minYPercent) newYPercent = minYPercent;
      }

      setDraggedElement(true);
      setDragData({
        x: newXPercent,
        y: newYPercent,
      });
      setInitialPosition({ x: newX, y: newY });

      setLocalElement({
        ...localElement,
        cardElement: {
          ...localElement.cardElement,
          frame: {
            ...frame,
            x: newXPercent,
            y: newYPercent,
          },
        },
      });
    };

    const onStopDrag = () => {
      window.removeEventListener('mousemove', onDrag);
      setDragging(false);
    };

    if (dragging) {
      window.addEventListener('mousemove', onDrag);
      window.addEventListener('mouseup', onStopDrag);
    }
    return () => {
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', onStopDrag);
    };
  }, [dragging, boundedX, boundedY]);

  useEffect(() => {
    // We have finished dragging the element when the drag value resets.
    // If at said point, we have dragged the element, and have drag data,
    // update the objects accordingly.
    if (!dragging && draggedElement && dragData) {
      onCardElementUpdate({
        cardElement: element.cardElement,
        data: {
          frame: {
            ...omit(frame, '__typename'),
            ...dragData,
          },
        },
      });
      setDraggedElement(false);
      setDragData(null);
    }
  }, [dragging, draggedElement, dragData]);

  const classes = classNames({
    'card-designer-card-element-drag-controls': true,
    'cursor-grabbing': dragging,
    [className]: !!className,
  });

  const handleClasses = classNames({
    'card-designer-card-element-move-handle': true,
    'cursor-grabbing': dragging,
  });

  const onStartDrag = event => {
    const { clientX, clientY } = event;
    setInitialMousePosition({ x: clientX, y: clientY });
    setDragging(true);
  };

  const onEndDrag = () => {
    setDragging(false);
  };

  return (
    <Fragment>
      <div
        role="button"
        tabIndex={0}
        aria-label="Drag"
        className={classes}
        onMouseDown={onStartDrag}
        onMouseUp={onEndDrag}
      />
      <div
        role="button"
        tabIndex={0}
        aria-label="Move"
        className={handleClasses}
        onMouseDown={onStartDrag}
        onMouseUp={onEndDrag}
      />
    </Fragment>
  );
};

CardDesignerCardElementDragControls.propTypes = propTypes;

export default CardDesignerCardElementDragControls;
